import { Component } from 'react';
import './InitSessionScreen.scss';

import i18n from "../i18n";
import { Trans } from 'react-i18next';
import {Box, CssBaseline, Typography, Button, Divider, Link} from "@material-ui/core";
import { Alert } from '@material-ui/lab';
import Config from "../common/Config";
import { LocaleService } from "../common/LocaleService";
import SiteAuth from "../common/SiteAuth";
import AppService from "../common/AppService";
import LegalConditionsBox from './LegalConditionsBox';
import Login from "./Login";
import appStoreBadge from "../assets/app-store-badge-black-120x40.png";
import googlePlayBadge from "../assets/google-play-badge.webp";
import appLogo from "../assets/app-icon.svg";
import googleMapsIcon from "../assets/google_maps_icon_130921.webp";
import appleMapsIcon from "../assets/apple-maps-icon.svg";
import UrlParamsService from "../common/UrlParamsService";

/** Enlace a un mapa externo equivalente al deep link. */
interface MapsLink {
    provider: string;
    url: string;
    icon: string;
}

/** Insignia de la tienda del sistema operativo. */
interface StoreBadge {
    url: string;
    image: string;
    alt: string;
}

interface InitSessionScreenState {
    /** La app corre como PWA. */
    asPWA: boolean;
    isInstalled: boolean;
    /** Si podemos cerrar la ventana, que casi nunca funciona. */
    canClose: boolean;
    showTestWarning: boolean;
    showDebug: boolean;
    showRegister: boolean;
}

// Store pages of the native app.
const APP_STORE_URL = 'https://apps.apple.com/es/app/gronze-maps/id1665609602';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.gronze.maps&hl=es';

class InitSessionScreen extends Component<object, InitSessionScreenState> {
    constructor(props: object) {
        super(props);
        this.state = {
            // detect if app act as PWA
            asPWA: AppService.isPwa(),
            // detect app not installed
            isInstalled: AppService.isInstalled(),
            // Whether we can close the window
            // which is not working mostly..
            canClose: false,
            // Test Warning
            showTestWarning: Config.config().app_test_warning,
            showDebug: !Config.isProduction(),
            showRegister: SiteAuth.canRegister()
        };
    }

    reload(): void {
        // Sin login no hay sesión que reiniciar: solo falta el token anónimo.
        // restartAuthentication abriría el login web (#673).
        if (!AppService.needsUserLogin()) {
            SiteAuth.clientAuthenticate();
            return;
        }
        // clean data and reload
        SiteAuth.restartAuthentication();
    }

    register(): void {
        // Start authentication w/ Registration.
        // SiteAuth.restartAuthentication();
        SiteAuth.resetAuthentication();
        SiteAuth.initAuthentication(true);
    }

    close(): void {
        window.close();
    }

    /**
     * External maps links equivalent to the deep link params, if they carry a
     * location. Fallback so the link is not a dead end while not logged in.
     *
     * Google Maps is always offered; Apple Maps only where it is native (not on
     * Android, where its link only opens a limited web page). Empty when there
     * is no location, as there is no equivalent to point to.
     *
     */
    mapsLinks(): MapsLink[] {
        if (!UrlParamsService.hasLocation()) {
            return [];
        }
        const params = UrlParamsService.getParams();
        const coords = `${params.lat},${params.lon}`;

        const links: MapsLink[] = [
            {
                provider: 'Google Maps',
                url: 'https://www.google.com/maps/search/?api=1&query=' + coords,
                icon: googleMapsIcon,
            },
        ];

        if (!AppService.isAndroid()) {
            links.push({
                // Apple reads 'q' as the pin label once 'll' centers the map, so
                // both carry the coordinates to match what the Google link shows.
                provider: 'Apple Maps',
                url: 'https://maps.apple.com/?ll=' + coords + '&q=' + coords,
                icon: appleMapsIcon,
            });
        }

        return links;
    }

    /**
     * Store badge for the device OS, if it has an app store.
     *
     */
    storeBadge(): StoreBadge | null {
        if (AppService.isIos()) {
            return { url: APP_STORE_URL, image: appStoreBadge, alt: 'App Store' };
        }
        if (AppService.isAndroid()) {
            return { url: GOOGLE_PLAY_URL, image: googlePlayBadge, alt: 'Google Play' };
        }
        return null;
    }

    /**
     * Whether to show just the app banners instead of the login.
     *
     * Only for card links on a device we can offer an app for, so desktop
     * (no store for its OS) always falls back to the login.
     *
     */
    showBannersOnly(): boolean {
        return !!(this.state.asPWA && UrlParamsService.isCardLink() && this.storeBadge());
    }

    render() {
        const siteLinks = Config.getSiteLinks(LocaleService.getLanguage());
        const storeBadge = this.storeBadge();
        const mapsLinks = this.mapsLinks();
        const bannersOnly = this.showBannersOnly();
        // Card links carry their own branding (the app icon in the CTA), so the
        // app title —and the header logo, hidden from App.js— would just repeat it.
        const showTitle = !UrlParamsService.isCardLink();
        // const appIcon = <SvgIcon component={MapsIcon} fontSize="large" />;

        return (
            <div id="initSessionCard" className={showTitle ? '' : 'noTitle'}>
                <CssBaseline />
                {
                    showTitle &&
                    <Typography  variant="h4" component="h1" gutterBottom  color="secondary" className="title">
                        { Config.getAppName() }<small> { Config.getAppTag() }</small>
                    </Typography>
                }

                <div className="content">
                    {
                        bannersOnly ?
                        <div className="cardLinkOptions">
                            <Box className="cardLinkIntro">
                                <Trans i18nKey="initSession.open_link_intro">Abre enlace en <strong>Gronze Maps</strong>…</Trans>
                            </Box>
                            {
                                storeBadge &&
                                <Link href={storeBadge.url} target="_blank" className="downloadCta">
                                    <img src={appLogo} alt="" className="ctaAppIcon" />
                                    <span className="ctaText">{ i18n.t('initSession.download_app_cta') }</span>
                                    <img src={storeBadge.image} alt={storeBadge.alt} className="ctaStoreBadge" />
                                </Link>
                            }
                            {
                                mapsLinks.length > 0 &&
                                <div className="cardLinkMaps">
                                    <Box className="cardLinkAlso">{ i18n.t('initSession.or_also') }</Box>
                                    {
                                        mapsLinks.map((m) =>
                                            <Link key={m.provider} href={m.url} target="_blank" className="optionRow">
                                                <span>{ i18n.t('initSession.open_in') } <strong>{ m.provider }</strong></span>
                                                <img src={m.icon} alt="" className="optionRowIcon" />
                                            </Link>
                                        )
                                    }
                                </div>
                            }
                        </div>
                        :
                        <div>
                            <Typography  variant="h5" component="h2" gutterBottom color="primary">
                                { i18n.t('initSession.title') }
                            </Typography>

                            <div className="gronzeInfo">

                                { this.state.showTestWarning &&
                                <Alert severity="info">
                                    <Trans i18nKey="initSessionScreen.warning">Test Warning</Trans>
                                </Alert>
                                }
                               <Box textAlign="left" fontWeight="fontWeightRegular" >
                                 <Typography variant="body1" component="div">
                                    <p>{ i18n.t('initSession.web_version') }</p>
                                 </Typography>
                               </Box>
                            </div>
                            <br/>
                            {
                                this.state.asPWA ?
                                    null
                                    :
                                    <Box textAlign="left" fontWeight="fontWeightRegular" >
                                        <strong>
                                            <Trans i18nKey="initSession.login_form_message">Introduce tu usuario y contraseña</Trans>
                                        </strong>
                                        <Login />
                                    </Box>
                            }
                            <Box textAlign="left" fontWeight="fontWeightRegular">
                                <strong><Trans i18nKey="help.init_session"><p>Inicia sesión en Gronze.com</p></Trans> </strong>
                            </Box>
                            <Box textAlign="center" fontWeight="fontWeightRegular">
                                <Button variant="contained" size="small" color="primary" className="" onClick={this.reload}>{ i18n.t('initSession.init') }</Button>
                            </Box>
                            {
                                this.state.showRegister ?
                                    <div><Box textAlign="left" fontWeight="fontWeightRegular">
                                        <strong>
                                            {i18n.t('help.register')}
                                        </strong>
                                    </Box>
                                        <Box textAlign="center" fontWeight="fontWeightRegular">
                                            <Button variant="contained" size="small" color="primary" className="" onClick={this.register}>{ i18n.t('initSession.register') }</Button>
                                        </Box></div>
                                    :
                                    null
                            }
                            {
                                this.state.canClose &&
                                <Box textAlign="center" fontWeight="fontWeightRegular">
                                    <Button color="secondary" className="big-button" onClick={this.close}>Cerrar</Button>
                                </Box>
                            }

                            <Box textAlign="left" fontWeight="fontWeightRegular">
                                <Trans i18nKey="help.pwa"><p>Explain PWA</p></Trans>
                            </Box>
                            <br/>

                            {
                                !this.state.isInstalled &&
                                <Box textAlign="left" fontWeight="fontWeightRegular">
                                    <p>
                                        {i18n.t('help.install_before')}
                                        <Link href={ siteLinks.help } target="_blank">{i18n.t('help.install_link')}</Link>
                                    </p>
                                </Box>
                            }
                        </div>
                    }

                    <div className="footer">
                        <Divider style={{marginTop: '10px'}} />
                        <LegalConditionsBox />
                        <Divider style={{marginBottom: '10px'}} />
                        <Typography variant="body2" component="p" align="center" >
                            { AppService.getAppInfoLabel() }
                        </Typography>
                        { this.state.showDebug &&
                            <Typography variant="body2" component="p" align="center" >
                                <small>
                                    { AppService.getDebugInfo() }
                                </small>
                            </Typography>
                        }
                    </div>
                </div>
            </div>
        );
    }

}

export default InitSessionScreen;
